import { useAdminContext } from '@/contexts/AdminContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import clsx from 'clsx';
import React from 'react';

// Simple Skeleton for Tailwind
const Skeleton = ({ className }: { className?: string }) => (
    <div className={clsx("animate-pulse bg-gray-200 rounded", className)} />
);

interface BillingSettingsProps {
    onClose: () => void;
}

const BillingSettings: React.FC<BillingSettingsProps> = ({ onClose }) => {
    const { userBilling, permissions } = useAuth();
    const { modal } = useAdminContext();

    // 权限检查
    const canView = permissions.checkPermissions([Permission.SUBSCRIPTION_VIEW]);
    const canManage = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);

    const {
        invoices, loading, userPlan, planText
    } = userBilling;
    const { invoices: data, pagination } = invoices || {};

    return (
        <div className="space-y-8">
            {!canView ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                    <p className="text-lg font-medium">Permission Denied</p>
                    <p className="text-sm mt-2">You do not have permission to view subscription details.</p>
                </div>
            ) : (
                <>
                    {/* Current Plan */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-text-main text-gray-900">Current plan</h3>
                        <div className="bg-white p-6 rounded-[20px] border border-gray-200 shadow-sm relative overflow-hidden">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h4 className="text-xl font-bold text-text-main text-gray-900">
                                            {planText.planDisplayText}
                                        </h4>
                                        {planText.badge ? (
                                            <span className="px-2 py-1 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                                                {planText.badge}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="text-text-sub text-gray-500 font-medium text-sm">
                                        {planText.expirationText}
                                    </p>
                                </div>

                                <button
                                    onClick={() => {
                                        if (!canManage) return;
                                        modal.openModal('pricing');
                                        onClose();
                                    }}
                                    disabled={!canManage}
                                    className={clsx(
                                        "px-4 py-2 rounded-lg text-sm font-bold transition-colors",
                                        canManage
                                            ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                                            : "border border-gray-200 text-gray-400 cursor-not-allowed"
                                    )}
                                >
                                    {canManage ? planText.buttonText : 'View Only'}
                                </button>
                            </div>
                        </div>
                    </div>
  
                    {/* Billing History */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-text-main text-gray-900">Billing History</h3>
                        <div className="bg-white rounded-[20px] border border-gray-200 overflow-hidden shadow-sm">
                            {/* Table Header */}
                            <div className="grid grid-cols-3 px-8 py-4 border-b border-gray-100 bg-gray-50/30">
                                <div className="text-xs font-bold text-text-sub text-gray-500 tracking-wider">Transaction ID</div>
                                <div className="text-xs font-bold text-text-sub text-gray-500 tracking-wider">Date</div>
                                <div className="text-xs font-bold text-text-sub text-gray-500 tracking-wider text-right">Total Amount</div>
                            </div>
            
                            <div className="divide-y divide-gray-50">
                                {data && data.length > 0 ? (
                                    data.map((item: any) => (
                                        <div key={item.id} className="grid grid-cols-3 px-8 py-4 hover:bg-gray-50/50 transition-colors">
                                            <div className="text-sm text-gray-900 font-medium truncate pr-4">{item.stripeInvoiceId || 'Unknown Task'}</div>
                                            <div className="text-sm text-gray-500">
                                                {new Date(item.createdAt).toLocaleString()}
                                            </div>
                                            <div className="text-sm text-gray-900 font-bold text-right">
                                                {(item.amountCents/100).toFixed(2)}
                                                {' '}
                                                {item.currency.toUpperCase()}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-16 text-center flex flex-col items-center justify-center">
                                        <p className="text-text-sub text-gray-500 font-medium">
                                            Billing history is not available.
                                        </p>
                                    </div>
                                )}
                        
                                {/* Load More Skeleton */}
                                {loading.invoices && pagination?.hasNext ? (
                                    <div className="p-4">
                                        <Skeleton className="w-full h-[70px]" />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default BillingSettings;
